/**
 * IndexedDB-based generation history storage
 * Keeps image URLs and metadata out of component state
 */

const DB_NAME = 'CutPreviewsDB';
const STORE_NAME = 'generations';
const DB_VERSION = 1;
const MAX_HISTORY_ITEMS = 50; // Store last 50 generations

export interface GenerationRecord {
  id: string;
  userId: string;
  originalPhotoUrl: string;
  resultPhotoUrl: string;
  hairstyleId?: string;
  hairstyleName?: string;
  facialHairId?: string;
  facialHairName?: string;
  createdAt: number;
}

/**
 * Open IndexedDB connection
 */
const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('userId', 'userId', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
  });
};

/**
 * Save a generation record to IndexedDB
 */
export const saveGeneration = async (record: GenerationRecord): Promise<void> => {
  const db = await openDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const request = store.put(record);
    
    request.onsuccess = () => {
      // Clean up old records
      cleanupOldRecords(db, record.userId);
      resolve();
    };
    request.onerror = () => reject(request.error);
    
    transaction.oncomplete = () => db.close();
  });
};

/**
 * Get recent generations for a user
 */
export const getRecentGenerations = async (
  userId: string, 
  limit: number = 10
): Promise<GenerationRecord[]> => {
  const db = await openDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('userId');
    
    const request = index.getAll(IDBKeyRange.only(userId));
    
    request.onsuccess = () => {
      const results = request.result || [];
      // Sort by createdAt descending and limit
      const sorted = results
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, limit);
      resolve(sorted);
    };
    request.onerror = () => reject(request.error);
    
    transaction.oncomplete = () => db.close();
  });
};

/**
 * Delete a generation record
 */
export const deleteGeneration = async (id: string): Promise<void> => {
  const db = await openDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const request = store.delete(id);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
    
    transaction.oncomplete = () => db.close();
  });
};

/**
 * Clean up old records, keeping only MAX_HISTORY_ITEMS per user
 */
const cleanupOldRecords = async (db: IDBDatabase, userId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('userId');
    
    const request = index.getAll(IDBKeyRange.only(userId));
    
    request.onsuccess = () => {
      const records = request.result || [];
      
      if (records.length > MAX_HISTORY_ITEMS) {
        // Sort by createdAt and delete oldest
        const sorted = records.sort((a, b) => b.createdAt - a.createdAt);
        const toDelete = sorted.slice(MAX_HISTORY_ITEMS);
        
        toDelete.forEach(record => {
          store.delete(record.id);
        });
      }
      
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all generations for a user
 */
export const clearUserGenerations = async (userId: string): Promise<void> => {
  const db = await openDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('userId');
    
    const request = index.getAllKeys(IDBKeyRange.only(userId));
    
    request.onsuccess = () => {
      const keys = request.result || [];
      keys.forEach(key => store.delete(key));
      resolve();
    };
    request.onerror = () => reject(request.error);
    
    transaction.oncomplete = () => db.close();
  });
};

/**
 * Get total count of generations for a user
 */
export const getGenerationCount = async (userId: string): Promise<number> => {
  const db = await openDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('userId');
    
    const request = index.count(IDBKeyRange.only(userId));
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    
    transaction.oncomplete = () => db.close();
  });
};
